import './style.css';
import {loadGameAssets} from './assets';
import {initializeDiscordActivity, type DiscordContext} from './discord';
import {TheDripGame} from './game';
import {readInputFrame, startInputCapture} from './input';
import {connectInstance, type InstanceSession} from './net';
import {CursorOverlay} from './cursors';
import {LobbyPanel} from './lobby';

const statusElement = document.querySelector<HTMLParagraphElement>('#status');
const playersElement = document.querySelector<HTMLSpanElement>('#players');
const scoreElement = document.querySelector<HTMLSpanElement>('#score');
const sizeElement = document.querySelector<HTMLSpanElement>('#size');
const powerElement = document.querySelector<HTMLSpanElement>('#power');
const ballsElement = document.querySelector<HTMLSpanElement>('#balls');
const canvas = document.querySelector<HTMLCanvasElement>('#game-canvas');
const overlayCanvas = document.querySelector<HTMLCanvasElement>('#cursor-overlay');
const powerButton = document.querySelector<HTMLButtonElement>('#touch-power');
const leftButton = document.querySelector<HTMLButtonElement>('#touch-left');
const rightButton = document.querySelector<HTMLButtonElement>('#touch-right');

if (!canvas || !overlayCanvas) {
  throw new Error('Missing game canvas');
}

function setStatus(message: string): void {
  if (statusElement) {
    statusElement.textContent = message;
  }
}

function setParticipantCount(count: number): void {
  if (playersElement) {
    playersElement.textContent = String(count);
  }
}

startInputCapture(canvas, powerButton ?? undefined, leftButton ?? undefined, rightButton ?? undefined);

const overlay = new CursorOverlay(overlayCanvas, () => ({
  w: canvas.clientWidth || canvas.width,
  h: canvas.clientHeight || canvas.height
}));

let session: InstanceSession | undefined;
let discordCtx: DiscordContext | undefined;

const lobby = new LobbyPanel('#lobby', {
  onQueue: () => {
    session?.queueMatch();
    lobby.setStatus('Looking for an opponent…');
  },
  onCancel: () => {
    session?.cancelMatch();
    lobby.setStatus('Match search cancelled.');
  },
  onInvite: () => {
    discordCtx?.openInviteDialog?.().catch(err => {
      console.warn(err);
      lobby.setStatus('Invite dialog unavailable.');
    });
  }
});

function connectForContext(ctx: DiscordContext): void {
  if (session) {
    session.close();
    session = undefined;
  }
  if (!ctx.user) return;

  session = connectInstance(
    {
      instanceId: ctx.instanceId,
      userId: ctx.user.id,
      username: ctx.user.globalName ?? ctx.user.username,
      accessToken: ctx.accessToken
    },
    {
      onStatus: msg => lobby.setStatus(msg),
      onWelcome: info => {
        lobby.setStatus(`Joined as P${info.seat + 1}.`);
      },
      onCursor: cursor => {
        if (ctx.user && cursor.userId === ctx.user.id) return;
        overlay.upsert(cursor);
      },
      onPresence: members => {
        overlay.setPresence(members, ctx.user?.id);
      },
      onMatched: match => {
        lobby.matched();
        lobby.setStatus(`Matched with ${match.peer.username}. Joining shared instance…`);
        ctx.instanceId = match.instanceId;
        ctx.inCall = true;
        ctx.participants = ctx.user ? [ctx.user] : [];
        lobby.update(ctx);
        connectForContext(ctx);
      }
    }
  );
}

function attachCursorBroadcast(ctx: DiscordContext): void {
  canvas?.addEventListener('pointermove', event => {
    if (!session) return;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;
    if (x < 0 || x > 1 || y < 0 || y > 1) return;
    session.sendCursor(x, y);
    void ctx;
  });
}

const assets = await loadGameAssets();
const game = new TheDripGame(canvas, assets, () => readInputFrame());
game.onStatus(status => {
  if (scoreElement) {
    scoreElement.textContent = String(Math.floor(status.score)).padStart(6, '0');
  }
  if (sizeElement) {
    sizeElement.textContent = status.size.toFixed(2);
  }
  if (powerElement) {
    powerElement.textContent = status.pendingPower ?? status.activePower ?? 'None';
  }
  if (ballsElement) {
    ballsElement.textContent = String(status.ballCount);
  }
});
game.start();

initializeDiscordActivity(setStatus, setParticipantCount)
  .then(ctx => {
    discordCtx = ctx;
    lobby.update(ctx);
    ctx.onParticipantsChanged(() => lobby.update(ctx));
    attachCursorBroadcast(ctx);
    connectForContext(ctx);
  })
  .catch(error => {
    console.error(error);
    setStatus(error instanceof Error ? error.message : 'Discord initialization failed.');
  });

